import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

import { RoomLoader } from '@/components/common/room-loader';
import { FeedDialog } from '@/features/feeds';
import { DoorExitDialog } from '@/features/door-exit';
import { BookDialog } from '@/features/books';
import { GitHubDialog } from '@/features/github';
import { LinkDialog } from '@/features/links';
import { FloatingMusicPlayer, MusicBootstrap } from '@/features/music';
import { ProfileDialog } from '@/features/profile';
import { SearchDialog } from '@/features/search';
import { useMediaQuery } from '@/hooks/use-media-query';
import { RoomCanvas, RoomFallback } from '@/scene';
import { useRoomStore } from '@/stores/room-store';
import type { CameraFocus, PanelId } from '@/types/room';
import { supportsWebGL } from '@/utils/webgl';

import { AppErrorBoundary } from './app-error-boundary';
import { ThemeSync } from './theme-sync';

function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: 1,
        staleTime: 5 * 60_000,
      },
    },
  });
}

type CameraFocusTarget = Exclude<CameraFocus, null>;

const panelCameraFocuses: Partial<Record<Exclude<PanelId, null>, CameraFocusTarget>> = {
  feed: 'bookshelf',
  book: 'bookshelf',
  github: 'laptop',
  profile: 'portrait',
  search: 'keyboard',
};

function RoomExperience() {
  const [hasWebGL] = useState(supportsWebGL);
  const [canvasReady, setCanvasReady] = useState(!hasWebGL);
  const [dollWordsReady, setDollWordsReady] = useState(!hasWebGL);
  const [roomRevealed, setRoomRevealed] = useState(false);
  const [roomDrawing, setRoomDrawing] = useState(false);
  const [roomSettled, setRoomSettled] = useState(false);
  const [sceneReady, setSceneReady] = useState(!hasWebGL);
  const panel = useRoomStore((state) => state.panel);
  const isDoorExitPromptOpen = useRoomStore((state) => state.isDoorExitPromptOpen);
  const focusObject = useRoomStore((state) => state.focusObject);
  const restoreCameraFocus = useRoomStore((state) => state.restoreCameraFocus);
  const isDesktop = useMediaQuery('(min-width: 720px)');
  const markCanvasReady = useCallback(() => setCanvasReady(true), []);
  const markDollWordsReady = useCallback(() => setDollWordsReady(true), []);
  const revealRoom = useCallback(() => {
    setRoomRevealed(true);
    if (hasWebGL) setRoomDrawing(true);
  }, [hasWebGL]);
  const finishRoomDrawing = useCallback(() => setRoomDrawing(false), []);
  const markSceneReady = useCallback(() => setSceneReady(true), []);
  const activeCameraFocus = isDoorExitPromptOpen
    ? 'door'
    : panel === null
      ? null
      : (panelCameraFocuses[panel] ?? null);

  useLayoutEffect(() => {
    if (!isDesktop || activeCameraFocus === null) {
      restoreCameraFocus();
      return;
    }
    focusObject(activeCameraFocus);
  }, [activeCameraFocus, focusObject, isDesktop, restoreCameraFocus]);

  useEffect(() => {
    if (!roomRevealed || roomSettled) return;
    const fallback = window.setTimeout(() => setRoomSettled(true), 560);
    return () => window.clearTimeout(fallback);
  }, [roomRevealed, roomSettled]);

  return (
    <main className="app-shell">
      <section
        className="room-stage"
        data-drawing={roomDrawing ? 'true' : 'false'}
        data-revealed={roomRevealed ? 'true' : 'false'}
        data-settled={roomSettled ? 'true' : 'false'}
        onTransitionEnd={(event) => {
          if (event.currentTarget === event.target && event.propertyName === 'opacity') {
            setRoomSettled(true);
          }
        }}
        aria-label="可交互三维房间"
      >
        {hasWebGL ? (
          <RoomCanvas
            onCanvasReady={markCanvasReady}
            onDollWordsReady={markDollWordsReady}
            onRevealComplete={finishRoomDrawing}
            onSceneReady={markSceneReady}
            revealActive={roomDrawing}
          />
        ) : (
          <RoomFallback />
        )}
        <FloatingMusicPlayer />
      </section>

      <ProfileDialog />
      <GitHubDialog />
      <DoorExitDialog />
      <BookDialog />
      <LinkDialog />
      <FeedDialog />
      <SearchDialog />
      <MusicBootstrap />
      <RoomLoader
        canvasReady={canvasReady}
        onReveal={revealRoom}
        sceneReady={sceneReady && dollWordsReady}
      />
    </main>
  );
}

export function App() {
  const [queryClient] = useState(createQueryClient);

  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeSync />
        <RoomExperience />
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}
